import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { signIn } from 'aws-amplify/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { ForgotPasswordModal } from '@/components/auth/ForgotPasswordModal';
import { ResetPasswordConfirmModal } from '@/components/auth/ResetPasswordConfirmModal';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [forgotPasswordModalOpen, setForgotPasswordModalOpen] = useState(false);
  const [resetPasswordConfirmModalOpen, setResetPasswordConfirmModalOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const navigate = useNavigate();

  const handleCodeSent = (email: string) => {
    setResetEmail(email);
    setForgotPasswordModalOpen(false);
    setResetPasswordConfirmModalOpen(true);
  };

  const handleResetPasswordBack = () => {
    setResetPasswordConfirmModalOpen(false);
    setForgotPasswordModalOpen(true);
  };

  const handleResetPasswordSuccess = () => {
    setResetPasswordConfirmModalOpen(false);
    setResetSuccess(true);
    setTimeout(() => setResetSuccess(false), 5000);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await signIn({ username: email, password });
      navigate('/app');
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to log in';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold">Welcome back</CardTitle>
          <CardDescription>
            Sign in to your FormTo.cc account
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {resetSuccess && (
              <p className="text-sm text-green-600 text-center p-2 bg-green-50 rounded">
                Password reset successfully. You can now log in with your new password.
              </p>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <button
                  type="button"
                  onClick={() => setForgotPasswordModalOpen(true)}
                  className="text-sm text-blue-600 hover:underline"
                  disabled={isLoading}
                >
                  Forgot password?
                </button>
              </div>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? 'Signing in...' : 'Sign in'}
            </Button>
            {error && (
              <p className="text-sm text-red-600 text-center">{error}</p>
            )}
            <p className="text-sm text-center text-gray-600">
              Don't have an account?{' '}
              <Link to="/register" className="text-blue-600 hover:underline">
                Sign up
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>

      {/* Forgot Password Modal */}
      <ForgotPasswordModal
        open={forgotPasswordModalOpen}
        onOpenChange={setForgotPasswordModalOpen}
        onBackToLogin={() => setForgotPasswordModalOpen(false)}
        onCodeSent={handleCodeSent}
      />

      {/* Reset Password Confirm Modal */}
      <ResetPasswordConfirmModal
        open={resetPasswordConfirmModalOpen}
        onOpenChange={setResetPasswordConfirmModalOpen}
        email={resetEmail}
        onSuccess={handleResetPasswordSuccess}
        onBackToEmail={handleResetPasswordBack}
      />
    </div>
  );
}
